<template>
  <div class="-mx-2">
    <div v-if="loading && !hasResults">
      <div class="my-6">
        <svg class="animate-spin h-5 w-5 text-brand mx-auto " xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div class="text-gray-400 text-center mt-2">Searching...</div>
      </div>
    </div>
    <div v-else>
      <div v-if="!hasResults && !ignoreEmptyState">
        <!-- This example requires Tailwind CSS v2.0+ -->
        <div class="text-center mt-6">
          <div class="text-gray-400 text-4xl"><i class="far fa-sad-tear"></i></div>
          <h3 class="mt-2 text-sm font-medium text-gray-900">No Search Results</h3>
          <p class="mt-1 text-sm text-gray-500">
            Try a different search term.
          </p>
        </div>
      </div>
      <div v-else>
        <div v-if="results.packages && results.packages.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Packages</h2>
          <a v-for="packageItem in results.packages" :href="packageItem._links.frontend.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>{{ packageItem.project }}:</strong> {{ packageItem.name }} ({{
                packageItem.reference
              }})</span>
            <span class="block text-gray-700 text-sm">{{ packageItem.client.name }}</span>
            <span class="block text-gray-500 text-sm">{{ packageItem.address }} | <strong>{{
                packageItem.status.name
              }}</strong></span>
          </a>
        </div>
        <div v-if="results.archives && results.archives.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Archived Packages</h2>
          <a v-for="packageItem in results.archives" :href="packageItem._links.frontend.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>{{ packageItem.project }}:</strong> {{ packageItem.name }} ({{
                packageItem.reference
              }})</span>
            <span class="block text-gray-700 text-sm">{{ packageItem.client.name }}</span>
            <span class="block text-gray-500 text-sm">{{ packageItem.address }} | <strong>{{
                packageItem.status.name
              }}</strong></span>
          </a>
        </div>
        <div v-if="results.companies && results.companies.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Companies</h2>
          <a v-for="company in results.companies" :href="company._links.classic.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>{{ company.name }}</strong></span>
            <div v-if="company.email" class="flex flex-row justify-between">
              <span class="block text-gray-700 text-sm" v-if="company.email">{{ company.email }}</span>>
            </div>
          </a>
        </div>
        <div v-if="results.people && results.people.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">People</h2>
          <a v-for="person in results.people" :href="person._links.classic.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>{{ person.name }}</strong></span>
            <div v-if="person.phone || person.email" class="flex flex-row justify-between">
              <span class="block text-gray-700 text-sm" v-if="person.email">{{ person.email }}</span>
              <span class="block text-gray-700 text-sm" v-if="person.phone">{{ person.phone }}</span>
            </div>
          </a>
        </div>
        <div v-if="results.materials && results.materials.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Materials</h2>
          <a v-for="material in results.materials" :href="material._links.frontend.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>{{ material.name }}</strong></span>
          </a>
        </div>
        <div v-if="results.equipment && results.equipment.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Equipment</h2>
          <a v-for="equipper in results.equipment" :href="equipper._links.frontend.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>{{ equipper.name }}</strong></span>
          </a>
        </div>
        <div v-if="results.pos && results.pos.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Purchase Orders</h2>
          <a v-for="po in results.pos" :href="po._links.classic.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>PO #{{ po.reference }}</strong></span>
            <span class="block text-gray-700 text-sm" v-if="po.supplier">{{ po.supplier }}</span>
            <span class="block text-gray-500 text-sm" v-if="po.raised">Raised: {{ po.raised }}</span>
            <span class="block text-gray-500 text-sm">£{{ po.costs.gross }}</span>
          </a>
        </div>
        <div v-if="results.pis && results.pis.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Purchase Invoices</h2>
          <a v-for="pi in results.pis" :href="pi._links.classic.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>PI #{{ pi.supplier_reference }}</strong></span>
            <span class="block text-gray-700 text-sm" v-if="pi.supplier">{{ pi.supplier }}</span>
            <span class="block text-gray-500 text-sm" v-if="pi.raised">Invoice Date: {{ pi.raised }}</span>
            <span class="block text-gray-500 text-sm">£{{ pi.costs.gross }}</span>
          </a>
        </div>
        <div v-if="results.sis && results.sis.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Sales Invoices</h2>
          <a v-for="si in results.sis" :href="si._links.classic.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>SI #{{ si.reference }}</strong></span>
            <span class="block text-gray-700 text-sm" v-if="si.client">{{ si.client }}</span>
            <span class="block text-gray-500 text-sm" v-if="si.raised">Invoice Date: {{ si.raised }}</span>
            <span class="block text-gray-500 text-sm">£{{ si.costs.gross }}</span>
          </a>
        </div>
        <div v-if="results.sos && results.sos.length">
          <h2 v-if="!noHeadings" class="text-xl font-medium text-gray-800 mb-1">Sales Orders</h2>
          <a v-for="so in results.sos" :href="so._links.classic.view"
             class="rounded text-brand hover:text-brand-light block mb-3 hover:bg-gray-50 p-2">
            <span class="block"><strong>SO #{{ so.client_reference }}</strong></span>
            <span class="block text-gray-700 text-sm" v-if="so.client">{{ so.client }}</span>
            <span class="block text-gray-500 text-sm" v-if="so.raised">Invoice Date: {{ so.raised }}</span>
            <span class="block text-gray-500 text-sm">£{{ so.costs.gross }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: [
    'results',
    'noHeadings',
    'ignoreEmptyState',
    'loading'
  ],
  watch: {},
  computed: {
    hasResults() {
      if (this.results.packages && this.results.packages.length) {
        return true;
      }
      if (this.results.archives && this.results.archives.length) {
        return true;
      }
      if (this.results.companies && this.results.companies.length) {
        return true;
      }
      if (this.results.people && this.results.people.length) {
        return true;
      }
      if (this.results.pos && this.results.pos.length) {
        return true;
      }
      if (this.results.pis && this.results.pis.length) {
        return true;
      }
      if (this.results.sis && this.results.sis.length) {
        return true;
      }
      if (this.results.sos && this.results.sos.length) {
        return true;
      }
      if (this.results.materials && this.results.materials.length) {
        return true;
      }
      if (this.results.equipment && this.results.equipment.length) {
        return true;
      }

      return false;
    }
  },
  methods: {},
  mounted: function () {

  }
}
</script>